import React, { useState, useEffect, useRef } from 'react';
import ISIN_SYMBOL_MAP from './isinSymbolMap';

/* ── Constants ────────────────────────────────────────────────────────────*/
const STATUS_CONFIG = {
  ORDER_RECEIVED: { label: 'Order Received', bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
  FILE_GENERATED: { label: 'File Generated', bg: '#fff7ed', color: '#c2410c', border: '#fed7aa' },
  ORDER_TRADED:   { label: 'Order Traded',   bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0' },
};

const EXCHANGE_STYLE = {
  NSE: { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe', btnBg: '#1d4ed8' },
  BSE: { bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0', btnBg: '#15803d' },
  MCX: { bg: '#fff7ed', color: '#c2410c', border: '#fed7aa', btnBg: '#c2410c' },
};

const DEFAULT_SLICE = {
  NIFTY: 1755, BANKNIFTY: 600, FINNIFTY: 1800,
  MIDCPNIFTY: 2800, SENSEX: 500, BANKEX: 600,
};

// Fallback only - used if the live /api/admin/lot-sizes fetch fails (network
// issue, etc). Normal operation always uses the live values fetched from
// lot_size_master via loadLotSizes() below, the same table the Python
// DropCopy sync service keeps current automatically.
const DEFAULT_LOT_SIZES = {
  NIFTY: 65, BANKNIFTY: 35, FINNIFTY: 65, MIDCPNIFTY: 120,
  SENSEX: 20, BANKEX: 30, NIFTYNXT50: 25,
};

const TODAY    = new Date().toISOString().slice(0, 10);
const WEEK_AGO = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

function fmtDT(ts) {
  if (!ts) return '--';
  return new Date(ts).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

/* ═══════════════════════════════════════════════════════════════════════════*/
export default function SqOffOrders() {

  const [orders,      setOrders]      = useState([]);
  const [stats,       setStats]       = useState({});
  const [loading,     setLoading]     = useState(true);
  const [refreshing,  setRefreshing]  = useState(false);
  const [selected,    setSelected]    = useState([]);
  const [filters,     setFilters]     = useState({
    exchange: 'ALL', segment: 'ALL', status: 'ALL',
    date_from: TODAY, date_to: TODAY,   // default: today so orders are immediately visible
  });
  const [toast,       setToast]       = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [sliceQty,    setSliceQty]    = useState(DEFAULT_SLICE);
  const [lotSizes,    setLotSizes]    = useState(DEFAULT_LOT_SIZES);
  const [showSlice,   setShowSlice]   = useState(false);
  const [sliceEdit,   setSliceEdit]   = useState({});
  const [sliceSaving, setSliceSaving] = useState(false);
  const [modal,       setModal]       = useState(null);

  // ── Use ref for filters so loadOrders always reads latest ────────────────
  const filtersRef = useRef(filters);
  useEffect(() => { filtersRef.current = filters; }, [filters]);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const token = () => localStorage.getItem('nb_admin_token');
  const authH = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token()}`,
  });

  /* ── loadOrders -- robust, always clears loading ──────────────────────────*/
  // silent=true is used for auto-refresh and the manual refresh button:
  // it updates the data in place WITHOUT toggling `loading`, so the table
  // never disappears/blinks. Only the very first mount-time load shows
  // the full "Loading orders..." state.
  const loadOrders = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    // Safety net: clear loading after 15s no matter what
    const safetyTimer = setTimeout(() => { setLoading(false); setRefreshing(false); }, 15000);
    try {
      const f      = filtersRef.current;
      const params = new URLSearchParams();
      if (f.exchange !== 'ALL') params.append('exchange',  f.exchange);
      if (f.segment  !== 'ALL') params.append('segment',   f.segment);
      if (f.status   !== 'ALL') params.append('status',    f.status);
      if (f.date_from)          params.append('date_from', f.date_from);
      if (f.date_to)            params.append('date_to',   f.date_to);

      // Fetch orders and stats independently -- one failure won't block the other
      // Load orders only -- stats computed from orders array to ensure consistency
      const ordersRes = await fetch('/api/admin/orders?' + params.toString(), { headers: authH() })
        .then(r => r.ok ? r.json() : Promise.reject(new Error(`Orders: ${r.status}`)));

      setOrders(ordersRes.orders || []);
      // Don't clear selection on a silent background refresh -- the admin
      // may have checkboxes ticked while a polling cycle runs in the background.
      if (!silent) setSelected([]);
    } catch (err) {
      console.error('[SqOffOrders] loadOrders error:', err.message);
      // Don't toast on every silent background failure -- would be noisy if
      // the network blips repeatedly. Only surface errors on explicit loads.
      if (!silent) {
        showToast('Failed to load orders: ' + err.message, 'error');
        setOrders([]);
      }
    } finally {
      clearTimeout(safetyTimer);
      setLoading(false);
      setRefreshing(false);
    }
  };

  /* ── loadSliceQty ──────────────────────────────────────────────────────────*/
  const loadSliceQty = async () => {
    try {
      const res  = await fetch('/api/admin/slice-qty', { headers: authH() });
      const data = await res.json();
      if (data.sliceQty) {
        const map = { ...DEFAULT_SLICE };
        data.sliceQty.forEach(r => { map[r.symbol] = r.slice_qty; });
        setSliceQty(map);
        setSliceEdit(map);
      }
    } catch {}
  };

  /* ── loadLotSizes ──────────────────────────────────────────────────────────
   * Fetches live lot sizes from lot_size_master (via the backend's new
   * GET /api/admin/lot-sizes), the same table the Python DropCopy sync
   * service keeps current automatically. Replaces a previously hardcoded
   * map that silently went stale for NIFTY (75 instead of the correct,
   * current 65). On any fetch failure, lotSizes simply stays at its
   * DEFAULT_LOT_SIZES initial value - the page keeps working exactly as
   * it always has, it just won't pick up the latest revision until the
   * fetch succeeds on a later load. */
  const loadLotSizes = async () => {
    try {
      const res  = await fetch('/api/admin/lot-sizes', { headers: authH() });
      const data = await res.json();
      if (data.lotSizes) {
        const map = { ...DEFAULT_LOT_SIZES };
        data.lotSizes.forEach(r => { map[r.symbol.toUpperCase()] = r.lot_size; });
        setLotSizes(map);
      }
    } catch {}
  };

  useEffect(() => { loadOrders(false); loadSliceQty(); loadLotSizes(); }, []); // eslint-disable-line

  // ── Auto-refresh every 10s, silently -- no blink, table updates in place ──
  useEffect(() => {
    const interval = setInterval(() => loadOrders(true), 10000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line

  /* ── saveSliceQty ──────────────────────────────────────────────────────────*/
  const saveSliceQty = async () => {
    setSliceSaving(true);
    try {
      await Promise.all(
        Object.entries(sliceEdit).map(([symbol, qty]) =>
          fetch(`/api/admin/slice-qty/${symbol}`, {
            method: 'PUT', headers: authH(),
            body: JSON.stringify({ slice_qty: qty }),
          })
        )
      );
      setSliceQty({ ...sliceEdit });
      showToast('Slice quantities updated.');
      setShowSlice(false);
    } catch {
      showToast('Failed to save.', 'error');
    } finally {
      setSliceSaving(false);
    }
  };

  /* ── Selection helpers ─────────────────────────────────────────────────────*/
  // Restored (14-Jul-2026): once a row's file has already been generated for
  // RMS, or the order is stale (its target position already went flat some
  // other way -- same is_stale signal the dealer/client Orders tabs show),
  // it must not be selectable for another file-generation run. Re-selecting
  // an already-generated order risks sending RMS a duplicate basket file;
  // re-selecting a stale order risks generating a file for a position that
  // no longer needs squaring off. This existed in an earlier version of
  // this page and was missing from the file this patch was built from.
  //
  // EXCEPTION (14-Jul-2026): the "mukil" admin login is exempt from this
  // restriction and can always select/download regardless of status or
  // staleness. All other current admins (rmsadmin, navia test, rms) and any
  // new admin created in the future are subject to the restriction -- this
  // checks the actual logged-in username from localStorage's `nb_admin`
  // record (set at login) rather than hardcoding a role, so it doesn't need
  // touching when new users are added later.
  const currentAdminUsername = () => {
    try {
      const raw = localStorage.getItem('nb_admin');
      return raw ? String(JSON.parse(raw).username || '').trim().toLowerCase() : '';
    } catch { return ''; }
  };
  const isUnrestrictedAdmin = () => currentAdminUsername() === 'mukil';
  const isSelectable = (o) => isUnrestrictedAdmin() || (!o.is_stale && o.status !== 'FILE_GENERATED' && o.status !== 'ORDER_TRADED');

  // Dealer Admin (support-desk role, 14-Jul-2026): this role must never be
  // able to select orders, generate, or download the RMS square-off basket
  // file at all -- "since they are support desk they no need this
  // functionality" (explicit requirement). Checked via the same nb_admin
  // localStorage record already used for the mukil exemption above, this
  // time reading `role` instead of `username`. Checkboxes and the Download
  // action bar are hidden entirely for this role below; the two backend
  // endpoints (/orders/generate-file and /orders/mark-file-generated) also
  // reject this role server-side via the new blockDealerAdmin middleware,
  // so this isn't just a UI-level hide.
  const currentAdminRole = () => {
    try {
      const raw = localStorage.getItem('nb_admin');
      return raw ? String(JSON.parse(raw).role || '').trim().toUpperCase() : '';
    } catch { return ''; }
  };
  const isDealerAdminRole = () => currentAdminRole() === 'DEALER_ADMIN';

  const toggleSelect = (id) => setSelected(s =>
    s.includes(id) ? s.filter(x => x !== id) : [...s, id]
  );
  const toggleAll = () => {
    const selectableIds = orders.filter(isSelectable).map(o => o.order_id);
    const allSelected = selectableIds.length > 0 && selectableIds.every(id => selected.includes(id));
    setSelected(allSelected ? [] : selectableIds);
  };

  // Prune selection whenever orders refresh (e.g. the 10s background poll) --
  // if a selected row's status flips to FILE_GENERATED/ORDER_TRADED, or it's
  // flagged stale, out from under an admin who already ticked it, it must be
  // deselected automatically rather than staying checked but disabled.
  useEffect(() => {
    setSelected(prev => {
      const stillValid = new Set(orders.filter(isSelectable).map(o => o.order_id));
      const next = prev.filter(id => stillValid.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [orders]); // eslint-disable-line

  const getSelectedExchange = () => {
    if (!selected.length) return null;
    const exs = [...new Set(orders.filter(o => selected.includes(o.order_id)).map(o => o.exchange))];
    return exs.length === 1 ? exs[0] : 'MIXED';
  };

  /* ── File generation helpers ───────────────────────────────────────────────*/
  const formatFileDate  = (d) => {
    const M = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return String(d.getDate()).padStart(2,'0') + M[d.getMonth()] + d.getFullYear();
  };
  const formatFileTime  = (d) => [d.getHours(),d.getMinutes(),d.getSeconds()].map(n=>String(n).padStart(2,'0')).join('-');
  const formatNEATExpiry = (ds) => {
    if (!ds) return '';
    const d = new Date(ds);
    const M = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
    return String(d.getDate()).padStart(2,'0') + M[d.getMonth()] + d.getFullYear();
  };
  const pad = (val, len) => String(val || '').padEnd(len, ' ');

  const extractBase = (sym) => {
    for (const idx of ['MIDCPNIFTY','BANKNIFTY','FINNIFTY','NIFTY','SENSEX','BANKEX'])
      if ((sym||'').toUpperCase().startsWith(idx)) return idx;
    return ((sym||'').match(/^([A-Z]+)/)||['',''])[1];
  };
  const extractCMSym = (s) => {
    if (!s) return '';
    if (s.includes(';')) {
      // Holdings symbol format is "COMPANY NAME ; ISIN" (see Dashboard.jsx /
      // DealerDashboard.jsx handleHoldingSquareOff). Root cause of NEAT
      // rejecting holdings square-off files: this used to just strip the
      // ISIN and keep the company name, which is not a valid NEAT scrip
      // code. Resolve the real trading symbol via the ISIN instead.
      const isin = s.split(';')[1]?.trim().toUpperCase();
      const resolved = isin && ISIN_SYMBOL_MAP[isin];
      if (resolved) return resolved;
      // ISIN not found in the master (e.g. new listing, symbol renamed
      // since the master was last refreshed) -- fall back to the old
      // behavior rather than send an empty/broken symbol. Still likely to
      // be rejected by NEAT same as before, but never worse than before.
      return s.split(';')[0].replace(/ EQ$|BE$/, '').trim();
    }
    return s.trim();
  };
  const splitSlices = (qty, sliceQ) => {
    if (!sliceQ || qty <= sliceQ) return [qty];
    const rows = []; let rem = qty;
    while (rem > 0) { rows.push(Math.min(rem, sliceQ)); rem -= Math.min(rem, sliceQ); }
    return rows;
  };
  const getNEATInstr = (sym, opt) => {
    const base  = extractBase(sym).toUpperCase();
    const isIdx = ['NIFTY','BANKNIFTY','FINNIFTY','MIDCPNIFTY','SENSEX','BANKEX'].includes(base);
    return opt ? (isIdx ? 'OPTIDX' : 'OPTSTK') : (isIdx ? 'FUTIDX' : 'FUTSTK');
  };
  const buildCMRow = (o, qty) => [
    // Col 0: Serial
    pad('1',13),
    // Col 1: BookType = 1 (Normal) -- ALWAYS 1
    pad('1',2),
    // Col 2: BuySellFlag -- 1=BUY(closing short), 2=SELL(closing long)
    // Confirmed: SHORT files all '1 ', LONG files all '2 ' (437+643 rows)
    pad(o.side==='BUY'?'1':'2',2),
    // Col 3: Symbol padded to 10
    pad(extractCMSym(o.symbol),10),
    // Col 4: Series = EQ
    pad('EQ',2),
    // Col 5: Instructions -- ALWAYS '1 ', NEVER the quantity
    // Previously had pad(qty,2) here which put the order quantity in this
    // field -- confirmed wrong against 1,080 real CM rows (all show '1 ')
    pad('1',2),
    // Col 6-8: blank
    pad('',11), pad('',2), pad('',9),
    // Col 9: Order type MKT
    pad('MKT',10),
    // Col 10: TrgPrice blank
    pad('',8),
    // Col 11: NetQty -- actual order quantity, padded to 9 (matches
    // buildMCSRow's NetQty field width exactly, and matches every other
    // fixed-width field in this row). Cross-verified 14-Jul-2026 against a
    // real RMS-accepted IDEA square-off row vs our own generated row for
    // the exact same order: the RMS-accepted file pads this field with
    // trailing spaces before the comma; ours previously sent it unpadded
    // ("13," instead of "13        ,"), which is the root cause of the
    // additional-space discrepancy the RMS reconciliation flagged.
    pad(String(qty), 9),
    // Col 12: DQ blank
    pad('',9),
    // Col 13: Member code
    pad('07708',12),
    // Col 14: Client type = 2
    pad('2',2),
    // Col 15: UCC
    pad(o.ucc,10),
    // Col 16-18: blank fields
    pad('',25),
    pad('',16),
    pad('',10),
  ].join(',');
  const buildFORow = (o, qty) => [
    // Verified against ACTUAL RMS-accepted NEAT FO file (23-Jun-2026) --
    // this is the live frontend implementation used for NSE FO files;
    // previously diverged from the backend's makeNeatFORow because they
    // were written and maintained independently. Every column below is
    // matched exactly against the real RMS reference file, not assumed.
    pad('1',         13),  // Col 0:  Serial
    'O',                   // Col 1:  Order type = letter O (normal) -- was '0', WRONG
    'U',                   // Col 2:  Order category
    // Col 3 is a FIXED CONSTANT '1' in every real RMS-accepted order, proven
    // across the entire bulk SHORT (724 rows) and LONG (708 rows) reference
    // files with zero exceptions - it does NOT encode buy/sell at all.
    // Previously this toggled with order.side, which meant SELL orders sent
    // '2' here - a value that never legitimately appears in any of the 1,432
    // real reference rows, almost certainly the actual cause of "Invalid
    // data in order entry" rejections for closing-long (SELL) square-offs.
    '1 ',                   // Col 3: Fixed constant -- NOT buy/sell
    '0',                   // Col 4:  Order sub-type
    // Col 5 is what actually encodes the real action, confirmed by the same
    // bulk data: every row in the SHORT file (closing shorts via BUY) shows
    // '1', every row in the LONG file (closing longs via SELL) shows '2'.
    // This is exactly the buy/sell logic previously (incorrectly) placed in
    // Col 3 above - moved here to match the real, proven format.
    (o.side==='BUY'?'1':'2') + ' ',  // Col 5: Buy/Sell (closing short=1, closing long=2)
    getNEATInstr(o.symbol, o.option_type),  // Col 6: OPTIDX/FUTIDX (no padding)
    pad(extractBase(o.symbol), 10),         // Col 7: Symbol pad 10
    pad(formatNEATExpiry(o.expiry_date), 10), // Col 8: Expiry DDMMMYYYY pad 10
    pad(o.strike_price ? String(Math.round(o.strike_price)) : '', 10), // Col 9: Strike pad 10
    o.option_type || '',   // Col 10: CE/PE (no padding)
    // RMS uses a constant '1' here in every real accepted order (confirmed
    // via bulk SHORT/LONG position files from RMS) -- the real total quantity
    // is carried entirely in Col 17, not here.
    '1 ',                   // Col 11: Quantity -- constant '1', NOT the lot count
    pad('',   11),         // Col 12: Blank, 11 spaces
    pad('',    2),         // Col 13: Blank, 2 spaces -- was 1, WRONG
    pad('',    9),         // Col 14: Blank, 9 spaces
    pad('MKT', 10),        // Col 15: Order Type MKT
    pad('',   10),         // Col 16: Price blank, 10 spaces -- was 8, WRONG
    // Col 17 = lot_size x number_of_lots in this row (i.e. real total share
    // quantity), confirmed against real RMS bulk data: e.g. BANKNIFTY rows
    // show 30/60/90/120... (clean multiples of the 30 per-lot size), proving
    // this field scales with quantity rather than being a fixed lot size.
    // Previously this only ever sent the static per-lot size (e.g. NIFTY=65)
    // regardless of how many lots qty actually represented -- correct only
    // by coincidence for exactly 1-lot orders, wrong for anything larger.
    pad(String((lotSizes[extractBase(o.symbol)] || 1) * qty), 9), // Col 17: total qty = lot_size x qty
    pad('',    9),         // Col 18: Blank, 9 spaces
    pad('07708', 12),      // Col 19: Member Code
    '2 ',                  // Col 20: Exchange (2=NSE)
    pad(o.ucc, 10),        // Col 21: UCC
    pad('',   24),         // Col 22: Blank, 24 spaces -- was 25, WRONG
    '0 ',                  // Col 23: Disclosed qty
    pad('',   16),         // Col 24: Blank, 16 spaces -- was 11, WRONG
    pad('',   12),         // Col 25: Blank, 12 spaces -- was empty, WRONG
  ].join(',');

  // MCX MCS basket file -- 22 comma-separated columns
  // Column mapping confirmed against real MCX bulk reference files:
  //   MCXFUTSHORT, MCXFUTLONG (21/25 rows each)
  //   MCXOPTIONSHORT, MCXOPTIONLONG (42 rows each)
  // BuySellFlag: 1=BUY (closing short), 2=SELL (closing long)
  //   -- same pattern as NSE NEAT: SHORT files always Col2='1', LONG always Col2='2'
  // NetQty (Col11): actual lot quantity (varies 1-27 in real files, NOT always 1)
  // SellPrice (Col20): strike price with 2 decimals for options, blank for futures
  // Participant (Col13): MCX member code 45345 (NOT 07708 which is NSE)
  const buildMCSRow = (o, qty) => {
    const isFuture  = !o.option_type || o.option_type === 'XX';
    const buySell   = (o.side === 'BUY' ? '1' : '2') + ' '; // 1=closing short, 2=closing long
    // Expiry: DDMMMYYYY e.g. 16JUL2026
    let expStr = '';
    if (o.expiry_date) {
      const d   = new Date(o.expiry_date);
      const dd  = String(d.getUTCDate()).padStart(2, '0');
      const mon = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'][d.getUTCMonth()];
      expStr = dd + mon + d.getUTCFullYear();
    }
    // Strike: decimal format e.g. '6100.00' for options, '' for futures
    const strikeStr = (!isFuture && o.strike_price)
      ? String(Number(o.strike_price).toFixed(2))
      : '';

    return [
      pad('1',       13),  // Col  0: ReferenceNumber
      '1 ',                // Col  1: BookType = 1 (Regular)
      buySell,             // Col  2: BuySellFlag 1=BUY 2=SELL
      pad(o.symbol,  10),  // Col  3: Symbol
      '',                  // Col  4: Series (blank for MCX)
      '1 ',                // Col  5: Instructions = 1
      pad('',        11),  // Col  6: GTD (blank)
      pad('',         2),  // Col  7: SpecialTerms (blank)
      pad('',         9),  // Col  8: MFQty (blank)
      pad('MKT',     10),  // Col  9: Price = MKT
      pad('',         8),  // Col 10: TrgPrice (blank)
      pad(String(qty), 9),  // Col 11: NetQty (actual lots, padded to 9)
      pad('',         9),  // Col 12: DQ (blank)
      pad('45345',   12),  // Col 13: Participant = MCX member code
      '2 ',                // Col 14: PROCLI = 2 (client)
      pad(o.ucc,     10),  // Col 15: UCC
      pad('',        25),  // Col 16: Remarks (blank)
      pad('',        16),  // Col 17: OrderNumber (blank)
      pad(expStr,    10),  // Col 18: ExpiryDate DDMMMYYYY
      o.option_type || '', // Col 19: Optiontype CE/PE or blank
      strikeStr,           // Col 20: SellPrice = strike for options, blank for futures
      pad('',        10),  // Col 21: ErrorCode (blank)
    ].join(',');
  };

  /* ── Download / generate file ──────────────────────────────────────────────*/
  const handleDownload = async () => {
    if (!selected.length) return showToast('Select at least one order.', 'error');
    const selEx = getSelectedExchange();
    if (selEx === 'MIXED') return showToast('Select orders from one exchange only.', 'error');
    setDownloading(true);
    try {
      const selOrders = orders.filter(o => selected.includes(o.order_id));
      const now = new Date();
      const fd  = formatFileDate(now);
      const ft  = formatFileTime(now);
      const nseCM = selOrders.filter(o => o.exchange === 'NSE' && o.segment === 'CM');
      const nseFO = selOrders.filter(o => o.exchange === 'NSE' && o.segment === 'FO');
      const bseCM = selOrders.filter(o => o.exchange === 'BSE' && o.segment === 'CM');
      const bseFO = selOrders.filter(o => o.exchange === 'BSE' && o.segment === 'FO');
      const mcxFO = selOrders.filter(o => o.exchange === 'MCX' && o.segment === 'FO');
      const files = [];

      if (nseCM.length > 0) {
        const lines = [];
        for (const o of nseCM) splitSlices(o.quantity, null).forEach(q => lines.push(buildCMRow(o, q)));
        files.push({ name: `BasketCM${fd}-${ft}.txt`, content: lines.join('\r\n'), type: 'text/plain' });
      }
      if (nseFO.length > 0) {
        const lines = [];
        for (const o of nseFO) {
          const sq = sliceQty[extractBase(o.symbol).toUpperCase()] || null;
          splitSlices(o.quantity, sq).forEach(q => lines.push(buildFORow(o, q)));
        }
        files.push({ name: `BasketFO${fd}-${ft}.txt`, content: lines.join('\r\n'), type: 'text/plain' });
      }
      if (bseCM.length > 0 || bseFO.length > 0) {
        // ── FIX: correct endpoint /orders/generate-file (was /orders/download) ──
        const res  = await fetch('/api/admin/orders/generate-file', {
          method: 'POST', headers: authH(),
          body: JSON.stringify({
            order_ids: [...bseCM,...bseFO].map(o => o.order_id),
            exchange: 'BSE',
          }),
        });
        const data = await res.json();
        if (data.files) data.files.forEach(f => files.push({
          name: f.filename, type: f.contentType,
          content: Uint8Array.from(atob(f.content), c => c.charCodeAt(0)),
        }));
      }

      // MCX MCS basket -- generated client-side same as NSE NEAT
      // Filename format: MCXDDMmmYYYY-HH-MM-SS.csv (e.g. MCX02Jul2026-12-32-20.csv)
      if (mcxFO.length > 0) {
        const header = 'ReferenceNumber,BookType,BuySellFlag,Symbol, Series,Instructions, GTD,SpecialTerms,MFQty,Price,TrgPrice,NetQty,DQ,Participant,PROCLI,Ucc,Remarks,OrderNumber,ExpiryDate,Optiontype,SellPrice,ErrorCode';
        const lines  = [header];
        for (const o of mcxFO) {
          // MCX does not use slice qty -- each order is one row at full quantity
          lines.push(buildMCSRow(o, o.quantity));
        }
        // Filename: MCXDDMmmYYYY-HH-MM-SS.csv
        const mcxDate = now.toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })
          .replace(/ /g,'').replace(',','');
        const mcxName = `MCX${mcxDate}-${ft}.csv`;
        files.push({ name: mcxName, content: lines.join('\r\n'), type: 'text/csv' });
      }

      if (files.length === 0) return showToast('No files generated. Check order segments.', 'error');

      for (const file of files) {
        const blob = new Blob([file.content], { type: file.type });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href = url; a.download = file.name;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
        await new Promise(r => setTimeout(r, 300));
      }

      // ── FIX: correct endpoint /orders/mark-file-generated (was /orders/mark-generated) ──
      await fetch('/api/admin/orders/mark-file-generated', {
        method: 'POST', headers: authH(),
        body: JSON.stringify({ order_ids: selected }),
      });

      showToast(`${files.length} file(s) downloaded for ${selected.length} orders.`);
      loadOrders();
    } catch (err) {
      showToast('Download failed: ' + err.message, 'error');
    } finally {
      setDownloading(false);
    }
  };

  /* ── Stat card drill-down ──────────────────────────────────────────────────*/
  const openModal = (type) => {
    const todayStr = TODAY;
    let filtered = [];
    if (type === 'today')     filtered = orders.filter(o => o.placed_at?.slice(0,10) === todayStr);
    if (type === 'received')  filtered = orders.filter(o => o.status === 'ORDER_RECEIVED');
    if (type === 'generated') filtered = orders.filter(o => o.status === 'FILE_GENERATED');
    if (type === 'traded')    filtered = orders.filter(o => o.status === 'ORDER_TRADED');
    setModal({ type, orders: filtered });
  };

  const setFilter  = (key, val) => setFilters(f => ({ ...f, [key]: val }));
  const inputStyle = {
    padding: '6px 10px', borderRadius: 6, border: '0.5px solid #e2e8f0',
    background: '#f8fafc', fontSize: 12, color: '#0f172a', outline: 'none',
  };
  const selEx = getSelectedExchange();
  const COLS  = '36px 90px 130px 100px 70px 70px 100px 80px 70px 50px 70px 110px 110px 100px';

  // Compute counts from loaded orders so stat cards always match drill-down
  const todayOrders    = orders.filter(o => o.placed_at?.slice(0,10) === TODAY);
  const receivedOrders = orders.filter(o => o.status === 'ORDER_RECEIVED');
  const generatedOrders= orders.filter(o => o.status === 'FILE_GENERATED');
  const tradedOrders   = orders.filter(o => o.status === 'ORDER_TRADED');

  const statCards = [
    { label: "Today's Orders", value: todayOrders.length,     color: '#1d4ed8', bg: '#eff6ff', type: 'today'     },
    { label: 'Order Received', value: receivedOrders.length,   color: '#7c3aed', bg: '#fdf4ff', type: 'received'  },
    { label: 'File Generated', value: generatedOrders.length,  color: '#c2410c', bg: '#fff7ed', type: 'generated' },
    { label: 'Order Traded',   value: tradedOrders.length,     color: '#15803d', bg: '#f0fdf4', type: 'traded'    },
  ];

  /* ═══════════════════════════════════════════════════════════════════════════
     RENDER -- unchanged from original except the 3 endpoint fixes above
  ═══════════════════════════════════════════════════════════════════════════ */
  return (
    <div style={{ fontFamily: "'Segoe UI', Arial, sans-serif" }}>

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 9999, background: toast.type === 'error' ? '#fff5f5' : '#f0fdf4', border: `0.5px solid ${toast.type === 'error' ? '#fecaca' : '#bbf7d0'}`, color: toast.type === 'error' ? '#dc2626' : '#15803d', padding: '10px 18px', borderRadius: 8, fontSize: 13, boxShadow: '0 4px 16px rgba(0,0,0,0.08)', maxWidth: 400 }}>
          {toast.msg}
        </div>
      )}

      {/* Slice Qty Modal */}
      {showSlice && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 14, width: 480, boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ padding: '18px 24px 14px', borderBottom: '1px solid #f3f4f6' }}>
              <div style={{ fontSize: 15, fontWeight: 600 }}>Slice Quantity Configuration</div>
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>Update exchange-defined slice quantities for index options</div>
            </div>
            <div style={{ padding: '16px 24px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {Object.entries(sliceEdit).map(([sym, qty]) => (
                  <div key={sym} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 14px' }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>{sym}</div>
                    <input type="number" value={qty} onChange={e => setSliceEdit(s => ({ ...s, [sym]: parseInt(e.target.value)||0 }))}
                      style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, fontWeight: 600, color: '#1d4ed8', boxSizing: 'border-box' }} />
                  </div>
                ))}
              </div>
              <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 12px', marginTop: 14, fontSize: 11, color: '#92400e' }}>
                ⚠ Update only when exchange revises slice quantities.
              </div>
            </div>
            <div style={{ padding: '14px 24px 18px', borderTop: '1px solid #f3f4f6', display: 'flex', gap: 10 }}>
              <button style={{ flex: 1, height: 38, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, color: '#374151', fontSize: 13, cursor: 'pointer' }} onClick={() => { setShowSlice(false); setSliceEdit({...sliceQty}); }}>Cancel</button>
              <button style={{ flex: 2, height: 38, background: '#1d4ed8', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }} onClick={saveSliceQty} disabled={sliceSaving}>{sliceSaving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Drill-Down Modal */}
      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 1000, maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ padding: '18px 24px 14px', borderBottom: '0.5px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 600, color: '#0f172a' }}>
                  {modal.type === 'today' ? "Today's Orders" : modal.type === 'received' ? 'Order Received' : modal.type === 'generated' ? 'File Generated' : 'Order Traded'}
                </div>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{modal.orders.length} records</div>
              </div>
              <button onClick={() => setModal(null)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 12, color: '#374151', cursor: 'pointer' }}>✕ Close</button>
            </div>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {modal.orders.length === 0 ? (
                <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8' }}>No records found.</div>
              ) : (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '100px 110px 1fr 70px 60px 60px 70px 140px 140px 120px 130px', padding: '10px 20px', background: '#f8fafc', borderBottom: '0.5px solid #e2e8f0', position: 'sticky', top: 0 }}>
                    {['UCC','Client','Symbol','Exch','Seg','Side','Qty',
                      modal.type === 'generated' ? 'File Gen Time' : 'Placed At',
                      modal.type === 'traded' ? 'Traded At' : 'Status',
                      'Placed By', modal.type === 'generated' ? 'RMS User' : 'Dealer ID'
                    ].map(h => (
                      <div key={h} style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</div>
                    ))}
                  </div>
                  {modal.orders.map((o, i) => {
                    const st = STATUS_CONFIG[o.status] || STATUS_CONFIG.ORDER_RECEIVED;
                    return (
                      <div key={o.order_id} style={{ display: 'grid', gridTemplateColumns: '100px 110px 1fr 70px 60px 60px 70px 140px 140px 120px 130px', padding: '11px 20px', alignItems: 'center', borderBottom: i < modal.orders.length - 1 ? '0.5px solid #f1f5f9' : 'none', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#0f172a' }}>{o.ucc}</div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>{o.client_name || '--'}</div>
                        <div style={{ fontSize: 12, fontWeight: 500, color: '#0f172a' }}>{o.symbol}</div>
                        <div><span style={{ fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 4, background: EXCHANGE_STYLE[o.exchange]?.bg, color: EXCHANGE_STYLE[o.exchange]?.color, border: `0.5px solid ${EXCHANGE_STYLE[o.exchange]?.border}` }}>{o.exchange}</span></div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>{o.segment}</div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: o.side === 'SELL' ? '#dc2626' : '#15803d' }}>{o.side}</div>
                        <div style={{ fontSize: 12, fontWeight: 600 }}>{o.quantity}</div>
                        <div style={{ fontSize: 11, color: '#374151' }}>{fmtDT(modal.type === 'generated' ? o.file_generated_at : o.placed_at)}</div>
                        <div>
                          {modal.type === 'traded'
                            ? <span style={{ fontSize: 11, color: '#15803d', fontWeight: 500 }}>{fmtDT(o.traded_at)}</span>
                            : <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 10, background: st.bg, color: st.color, border: `0.5px solid ${st.border}` }}>{st.label}</span>
                          }
                        </div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>{o.placed_by || 'CLIENT'}</div>
                        <div style={{ fontSize: 11, fontWeight: o.dealer_id ? 600 : 400, color: o.dealer_id ? '#1d4ed8' : '#94a3b8' }}>{o.dealer_id || '--'}</div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
            <div style={{ padding: '12px 24px', borderTop: '0.5px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 11, color: '#64748b' }}>{modal.orders.length} total records</span>
              <button onClick={() => setModal(null)} style={{ padding: '7px 20px', borderRadius: 7, border: 'none', background: '#1d4ed8', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Stat Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 20 }}>
        {statCards.map(c => (
          <div key={c.label}
            onClick={() => openModal(c.type)}
            onMouseEnter={e => { e.currentTarget.style.borderColor = c.color; e.currentTarget.style.boxShadow = `0 0 0 2px ${c.bg}`; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = 'none'; }}
            style={{ background: '#fff', border: '0.5px solid #e2e8f0', borderRadius: 8, padding: '14px 16px', cursor: 'pointer', transition: 'all 0.15s' }}>
            <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{c.label}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: c.color, textDecoration: 'underline', textUnderlineOffset: 3 }}>{c.value}</div>
              <span style={{ fontSize: 10, color: c.color, background: c.bg, padding: '2px 6px', borderRadius: 4, fontWeight: 600 }}>View {'->'}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ background: '#fff', border: '0.5px solid #e2e8f0', borderRadius: 10, padding: '14px 20px', marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <select value={filters.exchange} onChange={e => setFilter('exchange', e.target.value)} style={inputStyle}>
            {['ALL','NSE','BSE','MCX'].map(e => <option key={e} value={e}>{e === 'ALL' ? 'All Exchanges' : e}</option>)}
          </select>
          <select value={filters.segment}  onChange={e => setFilter('segment',  e.target.value)} style={inputStyle}>
            {['ALL','CM','FO'].map(s => <option key={s} value={s}>{s === 'ALL' ? 'All Segments' : s}</option>)}
          </select>
          <select value={filters.status}   onChange={e => setFilter('status',   e.target.value)} style={inputStyle}>
            <option value="ALL">All Status</option>
            <option value="ORDER_RECEIVED">Order Received</option>
            <option value="FILE_GENERATED">File Generated</option>
            <option value="ORDER_TRADED">Order Traded</option>
          </select>
          <input type="date" value={filters.date_from} onChange={e => setFilter('date_from', e.target.value)} style={inputStyle} />
          <input type="date" value={filters.date_to}   onChange={e => setFilter('date_to',   e.target.value)} style={inputStyle} />
          <button onClick={() => loadOrders(false)} style={{ ...inputStyle, background: '#1d4ed8', color: '#fff', cursor: 'pointer', border: 'none' }}>Apply</button>
          <button onClick={() => { setFilters({ exchange:'ALL', segment:'ALL', status:'ALL', date_from:'', date_to:'' }); setTimeout(() => loadOrders(false), 100); }} style={{ ...inputStyle, cursor: 'pointer' }}>Reset</button>
          <button onClick={() => loadOrders(true)} title="Refresh now (auto-refreshes every 10s in the background)"
            style={{ ...inputStyle, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ display: 'inline-block', transition: 'transform 0.6s linear', transform: refreshing ? 'rotate(360deg)' : 'rotate(0deg)' }}>↻</span>
            Refresh
          </button>
          <button onClick={() => { setSliceEdit({...sliceQty}); setShowSlice(true); }} style={{ ...inputStyle, cursor: 'pointer' }}>⚙ Slice Qty</button>
          <div style={{ flex: 1 }} />
          {selected.length > 0 && !isDealerAdminRole() && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 12, color: '#64748b' }}>
                {selected.length} selected
                {selEx === 'MIXED' && <span style={{ color: '#dc2626', marginLeft: 6 }}>-- one exchange only</span>}
              </span>
              <button onClick={handleDownload} disabled={downloading || selEx === 'MIXED'}
                style={{ padding: '7px 16px', borderRadius: 6, border: 'none', background: selEx === 'MIXED' ? '#f1f5f9' : (EXCHANGE_STYLE[selEx]?.btnBg || '#1d4ed8'), color: selEx === 'MIXED' ? '#94a3b8' : '#fff', fontSize: 12, fontWeight: 600, cursor: downloading || selEx === 'MIXED' ? 'not-allowed' : 'pointer' }}>
                {downloading ? 'Generating...' : `⬇ Download ${selEx !== 'MIXED' && selEx ? selEx + ' ' : ''}File`}
              </button>
            </div>
          )}
        </div>
        <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {Object.entries(sliceQty).map(([sym, qty]) => (
            <span key={sym} style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, background: '#f1f5f9', color: '#475569', border: '0.5px solid #e2e8f0' }}>{sym}: {qty.toLocaleString()}</span>
          ))}
        </div>
      </div>

      {/* Orders Table */}
      <div style={{ background: '#fff', border: '0.5px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: COLS, padding: '10px 16px', background: '#f8fafc', borderBottom: '0.5px solid #e2e8f0' }}>
          <div>{!isDealerAdminRole() && <input type="checkbox"
            checked={(() => { const sel = orders.filter(isSelectable).map(o=>o.order_id); return sel.length > 0 && sel.every(id => selected.includes(id)); })()}
            onChange={toggleAll} style={{ cursor: 'pointer' }} />}</div>
          {['UCC','Client','Symbol','Exch','Seg','Expiry','Strike','Type','B/S','Qty','Placed On','Traded On','Status'].map(h => (
            <div key={h} style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</div>
          ))}
        </div>
        {loading ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8' }}>Loading orders...</div>
        ) : orders.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8' }}>No sq-off orders found.</div>
        ) : orders.map((o, i) => {
          const st      = STATUS_CONFIG[o.status] || STATUS_CONFIG.ORDER_RECEIVED;
          const isSel   = selected.includes(o.order_id);
          const exStyle = EXCHANGE_STYLE[o.exchange] || {};
          const base    = extractBase(o.symbol);
          const sq      = sliceQty[base?.toUpperCase()];
          const split   = sq ? splitSlices(o.quantity, sq).length : 1;
          return (
            <div key={o.order_id} style={{ display: 'grid', gridTemplateColumns: COLS, padding: '11px 16px', alignItems: 'center', borderBottom: i < orders.length - 1 ? '0.5px solid #f1f5f9' : 'none', background: isSel ? '#f0f7ff' : '#fff' }}>
              <div>
                {!isDealerAdminRole() && (
                  <input type="checkbox" checked={isSel} disabled={!isSelectable(o)}
                    onChange={() => toggleSelect(o.order_id)}
                    title={!isSelectable(o) ? (o.is_stale ? 'Stale -- target position already flat, cannot generate a file for this order' : 'File already generated for this order') : undefined}
                    style={{ cursor: isSelectable(o) ? 'pointer' : 'not-allowed', opacity: isSelectable(o) ? 1 : 0.4 }} />
                )}
              </div>
              <div style={{ fontSize: 12, fontWeight: 500, color: '#0f172a' }}>{o.ucc}</div>
              <div style={{ fontSize: 11, color: '#64748b' }}>{o.client_name || '--'}</div>
              <div style={{ fontSize: 12, fontWeight: 500, color: '#0f172a' }}>
                {o.symbol}
                {sq && o.quantity > sq && <span style={{ fontSize: 9, background: '#fff7ed', color: '#c2410c', border: '0.5px solid #fed7aa', borderRadius: 3, padding: '1px 4px', marginLeft: 4 }}>{'->'}{split} rows</span>}
              </div>
              <div><span style={{ fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 4, background: exStyle.bg, color: exStyle.color, border: `0.5px solid ${exStyle.border}` }}>{o.exchange}</span></div>
              <div style={{ fontSize: 11, color: '#64748b' }}>{o.segment}</div>
              <div style={{ fontSize: 11, color: '#64748b' }}>{o.expiry_date ? new Date(o.expiry_date).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'2-digit'}) : '--'}</div>
              <div style={{ fontSize: 11, color: '#64748b' }}>{o.strike_price || '--'}</div>
              <div style={{ fontSize: 11, color: '#64748b' }}>{o.option_type  || '--'}</div>
              <div><span style={{ fontSize: 11, fontWeight: 600, color: o.side === 'SELL' ? '#dc2626' : '#15803d' }}>{o.side}</span></div>
              <div style={{ fontSize: 12, fontWeight: 500 }}>{o.quantity}</div>
              <div style={{ fontSize: 10, color: '#64748b' }}>{new Date(o.placed_at).toLocaleString('en-IN',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit'})}</div>
              <div style={{ fontSize: 10, color: o.traded_at ? '#15803d' : '#94a3b8' }}>{o.traded_at ? new Date(o.traded_at).toLocaleString('en-IN',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit'}) : '--'}</div>
              <div>
                <span style={{ fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 10, background: st.bg, color: st.color, border: `0.5px solid ${st.border}` }}>{st.label}</span>
                {!!o.is_stale && (
                  <span title="Target position already flat -- likely closed another way before RMS processed this order."
                    style={{ display: 'block', marginTop: 4, fontSize: 9, fontWeight: 600, padding: '2px 6px', borderRadius: 4, background: '#fef2f2', color: '#991b1b', border: '0.5px solid #fecaca', cursor: 'help' }}>
                    ⚠ Stale
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {orders.length > 0 && (
        <div style={{ textAlign: 'right', fontSize: 11, color: '#94a3b8', marginTop: 10 }}>
          {orders.length} orders . {selected.length} selected
          {selEx && selEx !== 'MIXED' && <span style={{ marginLeft: 8, color: '#1d4ed8' }}>. All selected: {selEx}</span>}
        </div>
      )}
    </div>
  );
}